import { Trans } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { hasFeatureFlags } from "../../../api/user-feature-flags";
import { DialogTitle } from "../../../components/ui-kit/base/dialog";
import { Button } from "../../../components/ui-kit/button";
import { Dialog } from "../../../components/ui-kit/dialog";
import { Form } from "../../../components/ui-kit/form/form";
import { WIDE_DIALOG_CLASS } from "../../../components/ui-kit/record-dialog-chrome";
import { connectedRoles } from "../../../constants/constants";
import { AdminConsole } from "../admin-console";
import {
  PERMISSION_SECTIONS_CLASS,
  PermissionSection,
  PermissionsPicker,
} from "../permissions/permissions-picker";
import {
  buildInitialFields,
  policyPatchBody,
} from "../permissions/policy-fields";
import {
  PermissionsMetadataFallback,
  usePermissionsMetadata,
} from "../permissions/use-permissions-metadata";
import { VisibilityPicker } from "../permissions/visibility-picker";
import { FeatureFlagsPicker } from "./feature-flags-picker";
import { Users, UsersItem } from "./users";

/**
 * Everything one user may do and see, as a stack of sections: permissions by
 * category, the visibility scope with its record pickers, and feature flags.
 *
 * All but the flags are pending edits - they accumulate in a draft and are
 * written to the access policy on Save, Cancel discards them. Feature flags are
 * live: each toggle writes immediately, which their section's hint says, as
 * they sit outside the Save / Cancel contract.
 *
 * A patient's own account is the exception: its policy is self-only and none of
 * the routers, operations or scopes apply to it, so those sections would be a
 * wall of boxes an admin must never tick. It gets the flags alone.
 */
export const UserPermissionsDialog = observer(
  function UserPermissionsDialog(props: {
    item: UsersItem;
    admin: AdminConsole;
    users: Users;
    onClose: () => void;
  }) {
    const { item, admin, users, onClose } = props;
    const flagsOnly = connectedRoles.includes(item.role);
    // A patient's account renders the flags alone and never reads the
    // metadata, so it never asks for it either.
    const { metadata, loadError, retry } = usePermissionsMetadata(!flagsOnly);

    const policy = admin.policyForUser(item.uuid);
    // The one condition both the fallback and the held-back flags read, so the
    // two cannot disagree about whether the dialog is still loading.
    const awaitingMetadata = !flagsOnly && !!policy && !metadata;
    const [{ initial, fields }] = useState(() => {
      const init = buildInitialFields(policy);
      return { initial: init, fields: observable({ ...init }) };
    });

    return (
      <Dialog
        open
        onOpenChange={(open) => !open && onClose()}
        className={flagsOnly ? undefined : WIDE_DIALOG_CLASS}
      >
        <DialogTitle className="border-border border-b pb-4 text-lg leading-normal">
          <Trans>Permissions: {item.username}</Trans>
        </DialogTitle>

        <Form
          className="flex flex-col flex-1 min-h-0"
          // A failed save rejects, which leaves the dialog open and reaches the
          // global error handler - it words the request error better than a
          // local catch could.
          onSubmit={async () => {
            const body = policyPatchBody(fields, initial);
            if (!policy || !body) {
              onClose();
              return;
            }
            await admin.updateAccessPolicy(policy.uuid, body);
            onClose();
          }}
        >
          <div className={PERMISSION_SECTIONS_CLASS}>
            {/* While the metadata loads the fallback stands alone, the flags
                held back though they are already here: one spinner, rather
                than flags the permissions then push down when they arrive. */}
            {awaitingMetadata ? (
              <PermissionsMetadataFallback
                loadError={loadError}
                retry={retry}
              />
            ) : (
              <>
                {flagsOnly ? null : !policy ? (
                  <p className="pb-4">
                    <Trans>
                      This user has no access policy, so permissions and
                      visibility cannot be edited here.
                    </Trans>
                  </p>
                ) : (
                  metadata && (
                    <>
                      <PermissionsPicker
                        fields={fields}
                        groups={metadata.groups}
                        rules={metadata.rules}
                      />
                      <VisibilityPicker
                        fields={fields}
                        groups={metadata.groups}
                        rules={metadata.rules}
                        admin={admin}
                        username={item.username}
                      />
                    </>
                  )
                )}

                <PermissionSection
                  title={<Trans>Feature flags</Trans>}
                  wide
                  hint={
                    // A patient's account has no Save for the flags to be
                    // outside.
                    !flagsOnly && (
                      <Trans>
                        Saved immediately. Cancel does not take a flipped flag
                        back.
                      </Trans>
                    )
                  }
                >
                  {hasFeatureFlags(item.feature_flags) ? (
                    <FeatureFlagsPicker
                      flags={item.feature_flags ?? {}}
                      onSave={(flag, update) =>
                        users.updateFeatureFlag(item.uuid, flag, update)
                      }
                    />
                  ) : (
                    <p className="col-span-full text-sm">
                      <Trans>This user has no feature flags.</Trans>
                    </p>
                  )}
                </PermissionSection>
              </>
            )}
          </div>

          <div className="border-border mt-4 flex justify-center gap-2 border-t pt-4">
            {flagsOnly ? (
              <Button type="button" onClick={onClose}>
                <Trans>Close</Trans>
              </Button>
            ) : (
              <>
                <Button type="submit" disabled={!policy || !metadata}>
                  <Trans>Save</Trans>
                </Button>
                <Button
                  type="button"
                  variant="subtle"
                  size="default"
                  onClick={onClose}
                >
                  <Trans>Cancel</Trans>
                </Button>
              </>
            )}
          </div>
        </Form>
      </Dialog>
    );
  },
);
